import { ErrorMessage, Field, Form, Formik } from "formik";
import Cookies from "js-cookie";
import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { FaEye } from "react-icons/fa";
import { FaEyeSlash } from "react-icons/fa6";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import OtpVerification from "./OtpVerification";
import ResetPasswordPage from "./ResetPasswordPage";
import AppContext from "../../context/AppContext";
import { generateToken } from "../../Push_notification/Notification";
import {
  getUserPermissions,
  loginUser,
  mapLoginResponseToUser,
  storeFcmToken,
} from "../../service/AuthService";
import { RiLoader3Fill } from "react-icons/ri";
import LangComp from "../../components/_app/langComp";
import translations from "../../components/common/Translations"
import CommonButton from "../../components/common/CommonButton";
const registrationSchema = (langMode) => Yup.object().shape({
  email: Yup.string().trim().required(translations[langMode].UsernameRequired || "Username is required"),
  password: Yup.string()
    .trim()
    .min(2, translations[langMode].PasswordMustBeAtLeast8Characters)
    .required(translations[langMode].PasswordRequired),
});

const languageData = [
  { id: 1, lang: "en", langText: "English" },
  { id: 2, lang: "ur", langText: "Urdu" },
  { id: 3, lang: "ar", langText: "Arabic" },
]

const LoginPage = () => {
  const { setUser, theme, fetchNotifications, toggleLangMode, langMode,setSelectedOrgId } = useContext(AppContext);
  const [resetEmail, setResetEmail] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const navbarRef = useRef(null);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      const trimmedValues = {
        email: (values.email || "").trim(),
        password: (values.password || "").trim(),
      };

      // API CALL
      console.log("Submitting login with values:", trimmedValues);
      const responseData = await loginUser(trimmedValues);
      
      const rememberCheckbox = document.getElementById("remember");
      const isChecked = rememberCheckbox?.checked;

      if (isChecked) {
        window.localStorage.setItem("isLoggedIn", "true");
      }

      if (responseData?.header?.code === 600) {
        const mapped = mapLoginResponseToUser(responseData.body?.value);
        if (!mapped?.accessToken) {
          toast.error("Login failed: missing token in response");
          return;
        }

        const { accessToken, ...userDetails } = mapped;

        setUser(userDetails);
        sessionStorage.setItem("userDetails", JSON.stringify(userDetails));
        localStorage.setItem("userDetails", JSON.stringify(userDetails));
        localStorage.setItem("accessToken", accessToken);

        if (userDetails?.organization?.organizationId) {
          localStorage.setItem("selectedOrgId", userDetails.organization.organizationId);
          setSelectedOrgId(userDetails.organization.organizationId);
        }

        localStorage.setItem("justLoggedIn", "true");

        const expires = new Date();
        expires.setTime(expires.getTime() + 7 * 60 * 60 * 1000);

        Cookies.set("accessToken", accessToken, { expires });
        Cookies.set("userType", userDetails.userType, { secure: true, sameSite: "strict" });
        Cookies.set("userId", userDetails.userId);
        Cookies.set("userName", userDetails.name);
        if (userDetails.userVisibility != null) {
          Cookies.set("userVisibility", userDetails.userVisibility);
        }
        if (userDetails.profilePic != null) {
          Cookies.set("profilePic", userDetails.profilePic);
        }
        Cookies.set("firstTimeLogin", userDetails.firstTimeLogin);
        Cookies.set("userEmail", userDetails.email);

        const permissionsResponse = await getUserPermissions(accessToken).catch((err) =>
          console.warn("User permissions fetch failed:", err)
        );

        if (permissionsResponse?.header?.code === 600 && permissionsResponse?.body?.value) {
          const permissionsValue = permissionsResponse.body.value;
          Cookies.set("userPermissions", JSON.stringify(permissionsValue), { expires });
          localStorage.setItem("userPermissions", JSON.stringify(permissionsValue));
        }

        navigate("/all-users");

        toast.success(
          `${userDetails.firstTimeLogin === "false"
            ? translations[langMode].welcomeback
            : translations[langMode].welcome
          } ${userDetails.name}`
        );

        const fcmToken = await generateToken();
        await storeFcmToken(fcmToken, accessToken).catch((err) =>
          console.warn("FCM token store failed:", err)
        );
        fetchNotifications(0);

      } else if (responseData?.header?.code === 612) {
        toast.error(translations[langMode].emailidorpass);
      } else if (responseData?.header?.code === 613) {
        // FIX: Triggering Reset Password Page
        setResetEmail(trimmedValues.email);
        setResetPassword(true);
      } else if (responseData?.header?.code === 608) {
        toast.error(translations[langMode].accessdenied);
      } else {
        toast.error(responseData?.body?.value || "Login failed");
      }
    } catch (error) {
      console.error("Login Error:", error);
      toast.error("Something went wrong. Check if the API URL is correct.");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsNavbarOpen(false);
      }
    };

    if (isNavbarOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isNavbarOpen]);

  const validationSchema = useMemo(() => {
    return registrationSchema(langMode);
  }, [langMode]);

  return (
    <>
      {!resetEmail && !resetPassword && (
        <div className="min-h-screen min-h-[100vh] flex flex-col lg:flex-row bg-white overflow-x-hidden">
          {/* Logo */}

          {/* LEFT SIDE - LOGIN FORM */}
          <div className="w-full lg:w-[40%] flex flex-1 items-center justify-center px-4 sm:px-6 py-6 sm:py-10 overflow-y-auto">
            <div className="w-full max-w-md flex flex-col">
              <div className="flex absolute top-4 left-4 sm:top-6 sm:left-6 lg:top-10 lg:left-10 items-center gap-3 mb-6 sm:mb-8">
                <img
                  src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344904/Bluefield_x0jmp9.png"
                  alt="Logo"
                  className="h-8 sm:h-10 object-contain"
                />
              </div>
              {/* Welcome Text */}
              <h2 className="text-2xl sm:text-3xl text-center font-bold text-gray-800 mb-2 mt-14 sm:mt-16">
                Welcome Back
              </h2>
              <p className="text-gray-500 mb-6 sm:mb-8 text-sm text-center">
                Enter your email and password to access your account.
              </p>
              <Formik
                initialValues={{ email: "", password: "" }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-6">

                    {/* Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[langMode]?.UsernameEmailAddress}
                      </label>
                      <Field
                        type="text"
                        name="email"
                        placeholder="Enter your email"
                        className="w-full min-w-0 border border-gray-300 rounded-lg py-2.5 sm:py-3 px-3 sm:px-4 text-base focus:ring-2 focus:ring-[#33373E] focus:outline-none transition"
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {translations[langMode]?.password}
                      </label>
                      <div className="relative">
                        <Field
                          type={showPassword ? "text" : "password"}
                          name="password"
                          placeholder="********"
                          className="w-full min-w-0 border border-gray-300 rounded-lg py-2.5 sm:py-3 px-3 sm:px-4 pr-10 text-base focus:ring-2 focus:ring-[#33373E] focus:outline-none transition"
                        />
                        <button
                          type="button"
                          onClick={togglePasswordVisibility}
                          className={`absolute top-1/2 -translate-y-1/2 text-gray-400 touch-manipulation text-base ${langMode === "ar" ? "left-3 sm:left-4" : "right-3 sm:right-4"}`}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <ErrorMessage
                        name="password"
                        component="div"
                        className="text-red-500 text-xs mt-1"
                      />
                    </div>

                    {/* Remember + Forgot */}
                    <div className="flex justify-between items-center text-sm">
                      <label className="flex items-center gap-2">
                        <input type="checkbox" id="remember" />
                        Remember me
                      </label>
                      {/* <span className="text-[#33373E] cursor-pointer hover:underline">
                        Forgot password?
                      </span> */}
                    </div>

                    {/* Login Button */}
                    <div className="w-full [&>div]:!block [&>div]:!w-full">
                      <CommonButton
                        type="submit"
                        disabled={isSubmitting || loading}
                        variant="primary"
                        className="w-full min-w-0 !justify-center touch-manipulation text-base font-semibold"
                      >
                        {loading ? (
                          <RiLoader3Fill className="animate-spin text-xl" />
                        ) : (
                          "Log In"
                        )}
                      </CommonButton>
                    </div>
                  </Form>
                )}
              </Formik>

              {/* Language Selector */}
              <div className="mt-6 sm:mt-10">
                <LangComp
                  isShowIcon={false}
                  languageDataList={languageData}
                  toggleLangMode={toggleLangMode}
                  isNavbarOpen={isNavbarOpen}
                />
              </div>
            </div>
          </div>

          {/* RIGHT SIDE - THEME PANEL */}
          <div className="hidden lg:flex lg:w-[60%] rounded-tl-2xl bg-[#236DB4] text-white flex-col items-center justify-start pt-12 xl:pt-16 px-8 xl:px-12 relative overflow-hidden flex-shrink-0">
            {/* TOP CONTENT */}
            <div className="text-center w-full">
              <h1 className="text-2xl xl:text-4xl text-white font-semibold mb-3 xl:mb-4">
                Effortlessly manage your team and operations.
              </h1>
              <p className="text-white text-base xl:text-xl">
                Log in to access your dashboard and manage everything in one place.
              </p>
            </div>

            {/* CENTER IMAGE */}
            <div className="flex justify-center items-center w-full min-h-0 flex-1">
              <img
                src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1771919289/Untitled_2_mxcsw8.png"
                alt="Dashboard Preview"
                className="shadow-orange-50 w-full max-w-6xl object-contain drop-shadow-2xl"
              />
            </div>

            {/* Light Gradient Overlay to Make Image Visible */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent pointer-events-none"></div>

          </div>

        </div>
      )}

      {resetEmail && !resetPassword && <OtpVerification emaill={resetEmail} />}
      {resetPassword && <ResetPasswordPage emaill={resetEmail} />}
    </>
  );
};

export default LoginPage;